import React from 'react';
import { Link } from 'react-router-dom';
import Navigation from './components/Navigation';
import SEO from './components/SEO';
import SiteFooter from './components/SiteFooter';
import { BreadcrumbStructuredData } from './components/StructuredData';
import { BOOKING_URL } from './tools/stackQuiz';
import { isValidEmail, utmFromSearch } from './whitePapers/whitePapers';
import { submitLead } from './leads/submitLead';
import { track } from './lib/analytics';

const SITE_URL = 'https://www.greyfielddata.com';
const URL = `${SITE_URL}/contact`;

const inputClass =
  'mt-1.5 w-full rounded-xl border border-slate-300 bg-white px-3.5 text-base text-slate-950 focus:border-[#1a4e94] focus:outline-none focus:ring-2 focus:ring-[#1a4e94]/30';
const labelClass = 'text-sm font-semibold text-slate-900';
const optional = <span className="font-normal text-slate-500">(optional)</span>;

type Status = 'idle' | 'submitting' | 'done';

function ContactForm() {
  const [status, setStatus] = React.useState<Status>('idle');
  const [name, setName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [company, setCompany] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [heardFrom, setHeardFrom] = React.useState('');
  const [website, setWebsite] = React.useState('');
  const [error, setError] = React.useState('');
  const doneRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (status !== 'done' || !doneRef.current) return;
    doneRef.current.focus({ preventScroll: true });
    doneRef.current.scrollIntoView({ block: 'nearest' });
  }, [status]);

  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const cleanEmail = email.trim();
    if (!isValidEmail(cleanEmail)) {
      setError('Enter a valid work email, like you@company.com.');
      return;
    }
    setError('');
    setStatus('submitting');
    const result = await submitLead({
      type: 'contact',
      email: cleanEmail,
      name: name.trim(),
      company: company.trim(),
      message: message.trim(),
      heardFrom: heardFrom.trim(),
      website,
      page: window.location.href,
      referrer: document.referrer || '',
      utm: utmFromSearch(window.location.search),
    });
    if (result.status === 400) {
      setError(result.error || 'Check your email address and try again.');
      setStatus('idle');
      return;
    }
    if (!result.clientNotified && !(result.status && result.status >= 200 && result.status < 300)) {
      setError('The message did not send. Try again, or email linus@greyfielddata.com.');
      setStatus('idle');
      return;
    }
    if (!website) track('generate_lead', { form: 'contact' });
    setStatus('done');
  }

  if (status === 'done') {
    return (
      <div ref={doneRef} tabIndex={-1} className="scroll-mt-28 scroll-mb-6 outline-none" data-track-location="contact_thanks">
        <div role="status" className="rounded-2xl bg-[#e6f4ec] p-5">
          <p className="font-semibold text-[#14532d]">Thanks. Your message is on its way.</p>
          <p className="mt-1.5 text-[15px] leading-6 text-[#14532d]">Linus will reply to {email.trim()}.</p>
        </div>
        <p className="mt-6 font-semibold text-[#0a1628]">Next step: pick a time to talk</p>
        <p className="mt-1.5 text-[15px] leading-6 text-slate-600">A free consultation call. Bring the report or project you described.</p>
        <a
          href={BOOKING_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-4 flex min-h-[52px] items-center justify-center rounded-xl bg-[#1a4e94] px-5 font-semibold text-white transition hover:bg-[#153f78]"
        >
          Book a free call
        </a>
        <Link to="/call-expectations" className="mt-3 inline-flex min-h-[44px] items-center text-sm font-semibold text-[#1a4e94] underline underline-offset-4">
          See what a first call is like
        </Link>
      </div>
    );
  }

  return (
    <form onSubmit={submit} noValidate className="space-y-4" aria-describedby="contact-privacy">
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="contact-name" className={labelClass}>Name {optional}</label>
          <input id="contact-name" name="name" type="text" autoComplete="name" maxLength={120} value={name} onChange={(e) => setName(e.target.value)} className={`h-12 ${inputClass}`} />
        </div>
        <div>
          <label htmlFor="contact-company" className={labelClass}>Company {optional}</label>
          <input id="contact-company" name="company" type="text" autoComplete="organization" maxLength={120} value={company} onChange={(e) => setCompany(e.target.value)} className={`h-12 ${inputClass}`} />
        </div>
      </div>
      <div>
        <label htmlFor="contact-email" className={labelClass}>Work email</label>
        <input
          id="contact-email"
          name="email"
          type="email"
          autoComplete="email"
          inputMode="email"
          required
          placeholder="you@company.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? 'contact-error' : undefined}
          className={`h-12 ${inputClass}`}
        />
        {error && <p id="contact-error" role="alert" className="mt-2 text-sm font-medium text-red-700">{error}</p>}
      </div>
      <div>
        <label htmlFor="contact-message" className={labelClass}>What do you need help with?</label>
        <textarea
          id="contact-message"
          name="message"
          rows={5}
          maxLength={5000}
          placeholder="The report, data, or project, and what a good outcome looks like."
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          className={`min-h-[132px] py-3 ${inputClass}`}
        />
      </div>
      <div>
        <label htmlFor="contact-heard" className={labelClass}>How did you hear about us? {optional}</label>
        <input id="contact-heard" name="heardFrom" type="text" maxLength={200} value={heardFrom} onChange={(e) => setHeardFrom(e.target.value)} className={`h-12 ${inputClass}`} />
      </div>
      {/* Honeypot: hidden from people, filled in by some bots. */}
      <div aria-hidden="true" className="absolute -left-[10000px] top-auto h-px w-px overflow-hidden">
        <label htmlFor="contact-website">Website</label>
        <input id="contact-website" name="website" type="text" tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} />
      </div>
      <button
        type="submit"
        disabled={status === 'submitting'}
        className="min-h-[52px] w-full rounded-xl bg-[#1a4e94] px-5 font-semibold text-white transition hover:bg-[#153f78] disabled:cursor-wait disabled:opacity-70"
      >
        {status === 'submitting' ? 'Sending...' : 'Send message'}
      </button>
      <p id="contact-privacy" className="text-[13px] leading-5 text-slate-500">
        Your details go to Linus Tse at Greyfield Data and are used to reply to you. See the{' '}
        <Link to="/privacy" className="text-[#1a4e94] underline underline-offset-2">privacy policy</Link>.
      </p>
    </form>
  );
}

export default function ContactPage() {
  return (
    <div className="min-h-screen overflow-x-hidden bg-white font-['Inter']">
      <SEO
        title="Contact Greyfield Data"
        description="Tell Greyfield Data what you need help with: Snowflake, Sigma, dbt, or reporting that does not add up. Linus Tse replies by email, or book a free call."
        url={URL}
        keywords={['contact Greyfield Data', 'Snowflake consultant contact', 'Sigma consultant contact', 'data consulting inquiry']}
      />
      <BreadcrumbStructuredData items={[{ name: 'Home', url: SITE_URL }, { name: 'Contact', url: URL }]} />
      <Navigation />

      <main className="mx-auto max-w-7xl px-4 pb-20 pt-28 sm:px-6 sm:pt-32 lg:px-8">
        <div className="grid items-start gap-10 lg:grid-cols-[minmax(0,1fr)_520px] lg:gap-16">
          <header className="min-w-0 lg:pt-6">
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[#1a4e94] sm:text-[13px]">Contact</p>
            <h1 className="mt-3 font-display text-[34px] font-bold leading-[1.12] tracking-tight text-[#0a1628] sm:text-5xl lg:text-[52px] lg:leading-[1.08]">
              Tell us what you need help with
            </h1>
            <p className="mt-4 max-w-2xl text-[17px] leading-relaxed text-slate-600 sm:mt-5 sm:text-xl">
              Snowflake, Sigma, dbt, or reporting that does not add up. Send a few lines and Linus Tse will reply by email, usually with a question or two and a time to talk.
            </p>
            <div className="mt-8 grid gap-3 sm:max-w-xl" data-track-location="contact_page">
              <div className="rounded-2xl bg-[#eaf2fd] p-[18px] sm:p-5">
                <p className="font-semibold text-[#0a1628]">Prefer to talk now?</p>
                <p className="mt-1.5 text-[15px] leading-6 text-slate-600">
                  Book a free consultation{' '}
                  <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="font-semibold text-[#1a4e94] underline underline-offset-4">on Linus's calendar</a>.
                </p>
              </div>
              <div className="rounded-2xl border border-slate-200 p-[18px] sm:p-5">
                <p className="font-semibold text-[#0a1628]">Or email directly</p>
                <p className="mt-1.5 text-[15px] leading-6 text-slate-600">
                  <a href="mailto:linus@greyfielddata.com" className="font-semibold text-[#1a4e94] underline underline-offset-4">linus@greyfielddata.com</a>
                </p>
              </div>
            </div>
          </header>

          <section aria-labelledby="contact-form-heading" className="relative min-w-0 rounded-3xl border border-slate-200 bg-white p-5 shadow-[0_24px_60px_-40px_rgba(10,22,40,0.55)] sm:p-8">
            <h2 id="contact-form-heading" className="mb-5 font-display text-xl font-bold leading-tight text-[#0a1628] sm:text-[22px]">Send a message</h2>
            <ContactForm />
          </section>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}
